import type { ExplorationHighlight } from '../../types/exploration';
import type { PackNode, PackNodeData } from '../../lib/bubble/packNodeTypes';

/**
 * Pure predicates behind bubble emphasis: which pack nodes a sidebar highlight matches, which
 * stay bright, and how to walk to a node's structural parent or owning instance.
 *
 * Split out of `bubbleEmphasis.ts` because none of it touches the DOM — it is the part of the
 * emphasis logic that can be reasoned about and tested in isolation.
 */

const skipParentTypes = new Set(['root', 'status', 'reason']);

export function nodeMatchesHighlight(
  data: PackNodeData,
  hl: NonNullable<ExplorationHighlight>
): boolean {
  if (data._isOverflow) {
    return (data._overflowChildren ?? []).some((child) =>
      nodeOrDescendantMatchesHighlight(child, hl)
    );
  }
  return nodeMatchesHighlightDirect(data, hl);
}

export function nodeMatchesHighlightDirect(
  data: PackNodeData,
  hl: NonNullable<ExplorationHighlight>
): boolean {
  if (hl.kind === 'status') {
    if ((data.type === 'status' || data.type === 'reason') && data.key === hl.key) {
      return true;
    }
    if (data.type === 'volume' && data.volume) {
      return data.volume.supportStatus === hl.key || data.volume.activationStatus === hl.key;
    }
    return false;
  }
  if (hl.kind === 'group') {
    if (data.name === hl.name && (!hl.type || data.type === hl.type)) {
      // Name-only group highlights must not match status/reason wrappers (e.g. sidebar
      // "Unattached" row vs inner reason bubble that shares the same label).
      if (!hl.type && (data.type === 'status' || data.type === 'reason')) {
        return false;
      }
      return true;
    }
    if (data.type === 'volume' && data.volume) {
      const ebsId = data.volume.ebsId;
      if (ebsId && hl.ebsIds?.includes(ebsId)) return true;
      if (hl.type === 'instance' && data.volume.instanceId === hl.name) return true;
    }
    return false;
  }
  if (hl.kind === 'volume') {
    return data.type === 'volume' && data.volume?.ebsId === hl.ebsId;
  }
  if (hl.kind === 'entityType') {
    // PackNodeData.type is an unconstrained string, so compare it against each
    // known HierarchyNodeType rather than widening the highlight's typed list.
    return !!data.type && hl.types.some((t) => t === data.type);
  }
  return false;
}

/** Walk a capped overflow subtree (HierarchyNode-shaped) for highlight matches. */
export function nodeOrDescendantMatchesHighlight(
  data: PackNodeData,
  hl: NonNullable<ExplorationHighlight>
): boolean {
  if (nodeMatchesHighlightDirect(data, hl)) return true;
  // Shallow hierarchy keeps volumes on `_volumes` with `children: null`.
  for (const vol of data._volumes ?? []) {
    if (nodeMatchesHighlightDirect({ type: 'volume', volume: vol }, hl)) return true;
  }
  for (const child of data.children ?? []) {
    if (nodeOrDescendantMatchesHighlight(child, hl)) return true;
  }
  return false;
}

export function isEmphasized(
  packNode: PackNode | null | undefined,
  hl: NonNullable<ExplorationHighlight>
): boolean {
  if (!packNode) return false;
  const chain = packNode.ancestors?.() ?? [packNode];
  return chain.some((ancestor) => nodeMatchesHighlight(ancestor.data, hl));
}

export function shouldStayBright(
  packNode: PackNode | null | undefined,
  hl: NonNullable<ExplorationHighlight>,
  pinTargets: Set<PackNode>
): boolean {
  return !!packNode && (isEmphasized(packNode, hl) || pinTargets.has(packNode));
}

/** Strict ancestors of every bright node — O(bright × depth), then O(1) membership checks. */
export function buildAncestorsOfBright(brightNodes: ReadonlySet<PackNode>): Set<PackNode> {
  const ancestors = new Set<PackNode>();
  for (const bright of brightNodes) {
    let p = bright.parent ?? null;
    while (p) {
      ancestors.add(p);
      p = p.parent ?? null;
    }
    // Also fold ancestors() — covers tests / nodes with incomplete parent links.
    const chain = bright.ancestors?.() ?? [];
    for (let i = 1; i < chain.length; i++) ancestors.add(chain[i]);
  }
  return ancestors;
}

/** Nearest structural ancestor (cluster, instance, nodeGroup, etc.) of a pack node. */
export function immediateStructuralParent(packNode: PackNode): PackNode | null {
  let p = packNode.parent;
  while (p) {
    const type = p.data?.type;
    if (type && !skipParentTypes.has(type)) return p;
    p = p.parent;
  }

  const chain = packNode.ancestors?.() ?? [packNode];
  for (let i = 1; i < chain.length; i++) {
    const type = chain[i].data?.type;
    if (type && !skipParentTypes.has(type)) return chain[i];
  }
  return null;
}

export function instanceNodeMatches(packNode: PackNode | null, instanceId: string): boolean {
  return packNode?.data?.type === 'instance' && packNode.data.name === instanceId;
}

export function isInsideSelectedInstance(packNode: PackNode | null, instanceId: string): boolean {
  if (!packNode) return false;
  const chain = packNode.ancestors?.() ?? [packNode];
  return chain.some((node) => instanceNodeMatches(node, instanceId));
}

